import DateRangeIcon from '@mui/icons-material/DateRange';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import ListIcon from '@mui/icons-material/List';
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Grid, IconButton, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { Link } from "react-router-dom";

import useGetRefundStatus from "../../constants/refundStatus";
import useGetSessionType from "../../constants/sessionType";
import { resources, roles } from "../../services/ability";
import { AbilityContext } from "../../services/Can";
import { getErrorMessage, post, put, SESSIONPOINTS, SESSIONS, USERS } from "../../services/Client";
import StartIconButton from "../buttons/StartIconButton";
import RenderBoolean from "../cellRender/RenderBoolean";
import RenderCell from "../cellRender/RenderCell";
import renderCellExpand from "../cellRender/renderCellExpand";
import { RenderUserUidRedirect } from "../cellRender/RenderUserRedirect";
import RenderVerificationRequiredCell from "../cellRender/RenderVerificationRequiredCell";
import { SettingsTooltip } from "../forms/CustomTooltip";
import CreateSessionModal from "../modals/CreateSessionModal";
import DateIntervalModal from "../modals/DateIntervalModal";
import NTMXGrid, {
    numberFormatter,
    secondsToDuration,
    timestampFormatter,
    timestampFormatterWithTimezone
} from "../NTMXGrid";
import { roundUp } from 'services/helper';
import { UserContext } from 'containers/App';

const storageKey = "session-table-columns";

export function useGetVerificationRequiredStrings() {
    const { t } = useTranslation();

    return [
        t('toBeRevised'),
        t('revised')
    ]
}

function getHomeCoordinates(params) {
    if (!getValue(params, 'homeAddressLng') && !getValue(params, 'homeAddressLat')) return ""
    return `${getValue(params, 'homeAddressLat') || ''} - ${getValue(params, 'homeAddressLng') || ''
        }`;
}

function getWorkCoordinates(params) {
    if (!getValue(params, 'workAddressLng') && !getValue(params, 'workAddressLat')) return ""
    return `${getValue(params, 'workAddressLat') || ''} - ${getValue(params, 'workAddressLng') || ''
        }`;
}

function getValue(params, field) {
    if (!params.row[field]) return ""
    return params.row[field]
}

function getSessionPoints(id, api) {
    let row = id && api && api.getRow(id)
    return row && row.sessionPoints ? row.sessionPoints : []
}

export const genericSaveSession = async (values, queryKey, enqueueSnackbar, queryClient, t, queryParams) => {
    enqueueSnackbar("saving...", { variant: "info" });

    if (values.id) await put(`${SESSIONS}/${values.id}/revalidate`, { body: values, params : queryParams })
        .then(() => enqueueSnackbar(t('saved'), { variant: "success" }))
        .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
        .finally(() => queryClient.invalidateQueries(queryKey));
    else
        await post(`${SESSIONS}/generate`, { body: values })
            .then(() => enqueueSnackbar(t('saved'), { variant: "success" }))
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => queryClient.invalidateQueries(queryKey));
}

export default function SessionTable({
    sessions = [],
    loading,
    interval,
    setInterval,
    queryKey,
    checkboxSelection,
    onSelectionModelChange,
    selectionModel,
    rightButton
}) {

    const { t } = useTranslation();
    let [localSessions, setLocalSessions] = useState([])
    let [toCheckFilter, setToCheckFilter] = useState(false)
    let [intervalModal, setIntervalModal] = useState(false)
    let [isEditingSession, setIsEditingSession] = useState(false)
    let [isCloningSession, setIsCloningSession] = useState(false)
    const { enqueueSnackbar } = useSnackbar()
    let queryClient = useQueryClient()
    const sessionType = useGetSessionType()
    const verificationRequiredStrings = useGetVerificationRequiredStrings()
    const refundStatus = useGetRefundStatus()
    const ability = useContext(AbilityContext)
    const canReadPrivateData = ability.can("read", resources.PRIVATEUSERDATA)
    const loggedUser = useContext(UserContext);

    const columns = [
        canReadPrivateData && {
            headerName: t('sessionID'),
            field: 'id',
            width: 310,
            //hide:true
        },
        canReadPrivateData && {
            headerName: t('userID'),
            field: 'uid',
            width: 280,
            //hide: true,
            renderCell: (params) => <RenderUserUidRedirect uid={params.row.uid} />
        },
        {
            headerName: t('sessionType'),
            field: 'type',
            width: 280,
            //hide: true,
            valueGetter: ({ value, row }) => (
                row.version === 2 || row.legs !== null ? //se ci sono legs allora è nuova o riconvalidato
                    row.legTypeList !== undefined ? row.legTypeList.split('|').map(id => sessionType.find(s => s.id + '' === id)?.name).join(', ') : "-"
                    :
                    value !== undefined ? sessionType.find(s => s.id === value).name : "-"
            )
            //renderCell: (params) => <RenderCell params = {params} saveEdit = {saveEditSession} type={"select"} options={sessionType}/>
        },
        loggedUser.userType !== roles.PINBIKER && {
            headerName: t('email'),
            field: 'email',
            width: 220,
            //hide: true
        },
        loggedUser.userType !== roles.PINBIKER && {
            headerName: t('phoneNumber'),
            field: 'phoneNumber',
            width: 170,
            //hide: true,
            renderCell: (params) => <RenderCell params={params}
                saveEdit={(id, field, newValue) => saveEditUser(params.row.uid, field, newValue)} />
        },
        loggedUser.userType !== roles.PINBIKER && {
            headerName: t('username'),
            field: 'username',
            width: 170,
            renderCell: (params) => <RenderUserUidRedirect username={params.value} uid={params.row.uid} />
        },
        loggedUser.userType !== roles.PINBIKER && {
            headerName: t('firstName'),
            field: 'firstName',
            width: 130,
            //hide: true
        },
        loggedUser.userType !== roles.PINBIKER && {
            headerName: t('lastName'),
            field: "lastName",
            width: 130,
            //hide: true
        },
        // {
        //     headerName: t('bikeType'),
        //     field: 'bikeType',
        //     width: 280,
        //     //hide: true,
        //     renderCell: (params) => <RenderCell params={params} saveEdit={saveEditSession}/>
        // },
        canReadPrivateData && {
            headerName: t('wheelDiameter'),
            field: 'wheelDiameter',
            width: 280,
            ...numberFormatter
            //hide: true,
            //renderCell: (params) => <RenderCell params = {params} saveEdit = {saveEditSession} type="number" decimals={2}/>
        },
        canReadPrivateData && {
            headerName: t('description'),
            field: 'description',
            width: 280,
            //hide: true,
            renderCell: (params) => <RenderCell params={params} saveEdit={saveEditSession} />
        },
        canReadPrivateData && {
            headerName: t('valid'),
            field: 'valid',
            width: 120,
            type: "boolean",
            renderCell: (params) => <RenderBoolean params={params} /*saveEdit = {saveEditSession}*/ />
        },
        canReadPrivateData && {
            headerName: t('certificated'),
            field: 'certificated',
            width: 120,
            type: "boolean",
            renderCell: (params) => <RenderBoolean params={params} /*saveEdit = {saveEditSession}*/ />
        },
        canReadPrivateData && {
            headerName: t('manuallyCertificated'),
            field: 'lastValidatedAt',
            width: 180,
            ...timestampFormatter
        },
        {
            headerName: t('startDate'),
            field: 'startTime_Date',
            width: 180,
            ...timestampFormatter
        },
        {
            headerName: t('startTime'),
            field: 'startTime_Time',
            width: 280,
            ...timestampFormatterWithTimezone
        },
        {
            headerName: t('endDate'),
            field: 'endTime_Date',
            width: 180,
            ...timestampFormatter
        },
        {
            headerName: t('endTime'),
            field: 'endTime_Time',
            width: 280,
            ...timestampFormatterWithTimezone,
        },
        {
            headerName: t('duration'),
            field: 'duration',
            width: 180,
            ...secondsToDuration
        },
        canReadPrivateData && {
            headerName: t('gyroDistance'),
            field: 'gyroDistance',
            width: 200,
            renderCell: (params) => <RenderCell params={params} measureUnit="km" type="number" />,
            ...numberFormatter
        },
        canReadPrivateData && {
            headerName: t('gpsDistance'),
            field: 'gpsDistance',
            width: 200,
            renderCell: (params) => <RenderCell params={params} measureUnit="km" type="number" />,
            ...numberFormatter
        },
        canReadPrivateData && {
            headerName: t('googleMapsDistance'),
            field: 'gmapsDistance',
            width: 200,
            renderCell: (params) => <RenderCell params={params} measureUnit="km" type="number" />,
            ...numberFormatter
        },
        {
            headerName: t('nationalPoints'),
            field: 'nationalPoints',
            width: 280,
            ...numberFormatter
            //hide: true,
            //renderCell: (params) => <RenderCell params = {params} saveEdit = {saveEditSession} type="number" decimals={0}/>
        },
        {
            headerName: t('initiativePoints'),
            field: 'sessionPoints',
            width: 280,
            //hide: true,
            valueGetter: ({ id, api }) => {
                let value = ""
                getSessionPoints(id, api).map(sp => value += `${sp.organizationTitle}: ${roundUp(sp.points, 2)}`)
                return value
            },
            renderCell: ({ row, colDef }) => {
                if (!row) return;
                if (row.sessionPoints) {
                    let values = []
                    row.sessionPoints.forEach((sp) => {
                        values.push({ showValue: sp.organizationTitle + ": " + sp.points })
                    })
                    return renderCellExpand(values, colDef)
                } else return renderCellExpand("", colDef)
            },
        },
        {
            headerName: t('euro'),
            field: 'euro',
            width: 280,
            //hide: true,
            valueGetter: ({ id, api }) => {
                let value = ""
                getSessionPoints(id, api).map(sp => value += `${sp.organizationTitle}: ${sp.euro ? parseFloat(sp.euro).toFixed(2) : " - "}`)
                return value
            },
            renderCell: ({ row, colDef }) => {
                if (!row) return;
                if (row.sessionPoints) {
                    let values = []
                    row.sessionPoints.forEach((sp) => {
                        let value = sp.euro ? parseFloat(sp.euro).toFixed(2) : " - "
                        values.push({ showValue: sp.organizationTitle + ": " + value + t('currency') })
                    })
                    return renderCellExpand(values, colDef)
                } else return renderCellExpand("", colDef)
            }
        },
        {
            headerName: t('refundStatus'),
            field: 'sessionPointsReimb',
            width: 280,
            //hide: true,
            valueGetter: ({ id, api }) => {
                let value = ""
                getSessionPoints(id, api).map(sp => value += `${sp.organizationTitle}: ${refundStatus.find(s => s.id === sp.refundStatus) ? refundStatus.find(s => s.id === sp.refundStatus).name : ""}`)
                return value
            },
            renderCell: ({ row, colDef }) => {
                if (!row) return;
                if (row.sessionPoints) {
                    let values = []
                    row.sessionPoints.forEach((sp) => {
                        values.push({ showValue: sp.organizationTitle + ": " + (refundStatus.find(s => s.id === sp.refundStatus) ? refundStatus.find(s => s.id === sp.refundStatus).name : "") })
                    })
                    return renderCellExpand(values, colDef)
                } else return renderCellExpand("", colDef)
            }
        },
        loggedUser.userType !== roles.PINBIKER && {
            headerName: t('co2'),
            field: 'co2',
            width: 120,
            //hide: true,
            renderCell: (params) => <RenderCell params={params} saveEdit={saveEditSession} measureUnit="g"
                type="number" />,
            ...numberFormatter
        },
        {
            headerName: t('initiativeKm'),
            field: 'initiativeKm',
            width: 280,
            //hide: true,
            valueGetter: ({ id, api }) => {
                let value = ""
                getSessionPoints(id, api).map(sp => value += `${sp.organizationTitle}: ${parseFloat(sp.distance).toFixed(2)}`)
                return value
            },
            renderCell: ({ row, colDef }) => {
                if (!row) return;
                if (row.sessionPoints) {
                    let values = []
                    row.sessionPoints.forEach((sp) => {
                        let value = parseFloat(sp.distance).toFixed(2)
                        values.push({ showValue: sp.organizationTitle + ": " + value + "km" })
                    })
                    return renderCellExpand(values, colDef);
                } else return renderCellExpand("", colDef);
            }
        },
        {
            headerName: t('nationalKm'),
            field: 'nationalKm',
            width: 280,
            //hide: true,
            renderCell: (params) => <RenderCell params={params} /*saveEdit = {saveEditSession}*/ measureUnit="km"
                type="number" />,
            ...numberFormatter
        },
        {
            headerName: t('homeWorkPath'),
            field: 'isHomeWorkPath',
            width: 280,
            //hide: true,
            type: "boolean",
            valueFormatter: ({
                id,
                api
            }) => getSessionPoints(id, api).map(sp => sp.homeWorkDistance > 0).reduce((a, b) => a || b, false),
            valueGetter: ({
                id,
                api
            }) => getSessionPoints(id, api).map(sp => sp.homeWorkDistance > 0).reduce((a, b) => a || b, false),
            renderCell: ({ id, api, colDef }) => {
                let row = api.getRow(id)
                if (row.sessionPoints) {
                    let values = []
                    row.sessionPoints.forEach((sp) => {
                        values.push({
                            key: sp.organizationTitle, value: !!sp.homeWorkDistance, id: sp.organizationId,
                            type: "boolean", field: "isHomeWorkPath",
                            showValue: sp.organizationTitle + ": ", booleanValue: !!sp.homeWorkDistance
                        })
                    })
                    return renderCellExpand(values, colDef)
                } else return renderCellExpand("", colDef)

            }
        },
        canReadPrivateData && {
            headerName: t('homeAddress'),
            field: 'homeAddress',
            width: 250,
            //hide: true,
            //renderCell: (params) => <RenderCell params = {params} saveEdit = {saveEdit}/>
        },
        canReadPrivateData && {
            field: 'homeAddressCoordinates',
            headerName: t('homeCoordinates'),
            width: 250,
            valueGetter: getHomeCoordinates,
            //hide: true,
        },
        canReadPrivateData && {
            headerName: t('workAddress'),
            field: 'workAddress',
            width: 250,
            //hide: true,
            //renderCell: (params) => <RenderCell params = {params} saveEdit = {saveEdit}/>
        },
        canReadPrivateData && {
            field: 'workAddressCoordinates',
            headerName: t('workCoordinates'),
            width: 250,
            valueGetter: getWorkCoordinates,
            //hide: true,
        },
        loggedUser.userType !== roles.PINBIKER && {
            headerName: t('multiplier'),
            field: 'initiativeMultiplier',
            width: 280,
            //hide: true,
            valueGetter: ({ id, api }) => {
                let value = ""
                getSessionPoints(id, api).map(sp => value += `${sp.organizationTitle}: x${sp.multiplier || 1}`)
                return value
            },
            renderCell: params => {
                if (params.row.sessionPoints) {
                    let values = []
                    params.row.sessionPoints.map((sp) => {
                        values.push({/*key: sp.organizationTitle, value: parseFloat(sp.multiplier),
                            startAdornment: "x", type: "number", id: sp.id, field: "multiplier",*/
                            showValue: sp.organizationTitle + ": x" + (sp.multiplier || "-")
                        })
                    })
                    return renderCellExpand(values, params.colDef)
                } else return renderCellExpand("", params.colDef)
            }
        },
        canReadPrivateData && {
            headerName:  t('revisedBy'),
            field: "lastValidatedBy",
            filterable: false,
            sortable: false,
            width: 250,
        },
        canReadPrivateData && {
            headerName: t('toBeRevised'),  //null: non da revisionare, true: da revisionare, false: già revisionata
            field: "verificationRequired",
            width: 250,
            //hide: true,
            valueGetter: ({ value }) => value === true ? verificationRequiredStrings[0] : value === false ? verificationRequiredStrings[1] : "",
            renderCell: params => params.value &&
                <RenderVerificationRequiredCell params={params} saveEdit={saveEditSession} />
        },
        canReadPrivateData && {
            headerName: t('revisedType'),
            field: "verificationRequiredType",
            width: 250,
            //hide: true,
            valueGetter: ({ id, api }) => {
                let value = ""
                if (api.getRow(id).verificationRequiredType)
                    api.getRow(id).verificationRequiredType.map(vt => value += `${vt.value}`)
                return value
            },
            renderCell: ({ row, colDef }) => {
                if (!row) return;
                if (row.verificationRequiredType) {
                    let values = []
                    row.verificationRequiredType.forEach(e => values.push({ showValue: e.value }))
                    return renderCellExpand(values, colDef)
                } else return ""
            }
        },
        canReadPrivateData && {
            headerName: t('revisedNote'),
            field: "verificationRequiredNote",
            width: 250,
            //hide: true,
            renderCell: ({ value, colDef }) => {
                if (value) {
                    return renderCellExpand([{ showValue: value }], colDef)
                } else return ""
            }
        },
        canReadPrivateData && {
            headerName: t('sensorStartBattery'),
            field: 'startBattery',
            width: 280,
            renderCell: (params) => params.value === 0 || params.value ? params.value + "%" : "",
            ...numberFormatter
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('sensorEndBattery'),
            field: 'endBattery',
            width: 280,
            renderCell: (params) => params.value === 0 || params.value ? params.value + "%" : "",
            ...numberFormatter
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('phoneStartBattery'),
            field: 'phoneStartBattery',
            width: 280,
            renderCell: (params) => params.value === 0 || params.value ? params.value + "%" : "",
            ...numberFormatter
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('phoneEndBattery'),
            field: 'phoneEndBattery',
            width: 280,
            renderCell: (params) => params.value === 0 || params.value ? params.value + "%" : "",
            ...numberFormatter
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('sensor'),
            field: "sensor",
            width: 250,
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('sensorName'),
            field: "sensorName",
            width: 250,
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('firmware'),
            field: "firmware",
            width: 250,
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('appVersion'),
            field: "appVersion",
            width: 250,
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('platform'),
            field: "platform",
            width: 250,
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('deviceModel'),
            field: "phoneModel",
            width: 250,
            //hide: true
        },
        canReadPrivateData && {
            headerName: t('sentGrifo'),
            field: "forwardedAt",
            width: 250,
            //hide: true,
            ...timestampFormatter
        },
        canReadPrivateData && {
            headerName: t('details'),
            field: "polyline",
            width: 140,
            sortable: false,
            disableColumnMenu: true,
            resizable: false,
            disableExport: true,
            hideable: false,
            renderCell: (params) => <Grid container direction={"row"}>
                <SettingsTooltip title={t('information')}><Link to={"/sessions/" + params.row.id}
                    target="_blank"><IconButton><VisibilityIcon /></IconButton></Link></SettingsTooltip>
                <SettingsTooltip title={t('clone')}><IconButton onClick={() => setIsCloningSession(params.row)}
                    aria-label="delete"><FilterNoneIcon /></IconButton></SettingsTooltip>
                <SettingsTooltip title={t('edit')}><IconButton onClick={() => setIsEditingSession(params.row)}
                    aria-label="edit"><EditIcon /></IconButton></SettingsTooltip>
            </Grid>
        }
    ].filter(Boolean);

    useEffect(() => {
        let newSessions;
        if (toCheckFilter) newSessions = getFilteredSessions(sessions)
        else newSessions = sessions.slice()
        newSessions.sort((a, b) => b.endTime - a.endTime)
        setLocalSessions(newSessions)
    }, [sessions])

    const saveEditSession = (id, field, value) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        put(SESSIONS, { body: { [field]: value }, elem: id })
            .then(() => enqueueSnackbar(t('saved'), { variant: "success" }))
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => queryClient.invalidateQueries(queryKey));
    }

    const saveEditUser = (uid, field, value) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        put(USERS, { body: { [field]: value }, elem: uid })
            .then(() => {
                enqueueSnackbar(t('saved'), { variant: "success" });
            })
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => queryClient.invalidateQueries(queryKey));
    }

    const saveSession = (values, queryParams) => {
        return genericSaveSession(values, queryKey, enqueueSnackbar, queryClient, t, queryParams);
    }

    const onChangeToCheck = () => {
        if (toCheckFilter) setLocalSessions(sessions)
        else setLocalSessions(getFilteredSessions(sessions))
        setToCheckFilter(!toCheckFilter)
    }

    const getFilteredSessions = (sessions) => {
        return sessions ? sessions.filter(s => s.verificationRequired && s.verificationRequired === true) : []
    }

    return <>
        <NTMXGrid
            key={storageKey}
            columns={columns}
            rows={localSessions || undefined}
            // rows={canReadPrivateData ? (localSessions || undefined) : localSessions && localSessions.filter(s => s.certificated)}
            title={t('sessions')}
            getRowId={(row) => localSessions && row.id}
            rightButton={<Grid container justifyContent={"flex-end"}>

                {rightButton}
                <StartIconButton
                    onClick={() => setIntervalModal(true)}
                    title={
                        <Typography variant="subtitle1" component="div"
                            style={{ fontWeight: 600, paddingLeft: "2px", paddingTop: "1px" }}>
                            {dayjs(interval.start).format("DD/MM/YYYY") + " - " + dayjs(interval.end).format("DD/MM/YYYY")}
                        </Typography>
                    }
                    startIcon={<DateRangeIcon />}
                />

                {canReadPrivateData && <StartIconButton
                    onClick={onChangeToCheck}
                    title={!toCheckFilter ? t('toCheck') : t('fullList')}
                    startIcon={!toCheckFilter ? <FilterListIcon /> : <ListIcon />} />
                }
            </Grid>}
            initialState={{ pinnedColumns: { right: ['polyline'] } }}
            loading={loading}
            checkboxSelection={checkboxSelection} onSelectionModelChange={onSelectionModelChange}
            selectionModel={selectionModel}
        />

        <DateIntervalModal interval={interval} open={intervalModal} onClose={() => setIntervalModal(false)}
            onSubmit={(values) => setInterval(values)} />

        {
            (!!isCloningSession || !!isEditingSession) && <CreateSessionModal
                open={!!isCloningSession || !!isEditingSession}
                onClose={() => {
                    setIsCloningSession(false);
                    setIsEditingSession(false)
                }}
                onSubmit={saveSession}
                session={isCloningSession || isEditingSession}
                type={isEditingSession ? "edit" : isCloningSession ? "clone" : "create"}
            />
        }
    </>
}
